// ImportModal.jsx

import React, { useMemo, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
  buildExistingImportTitleSet,
  buildImportableFields,
  buildImportPreview,
  buildInitialMappings,
  buildImportTemplateCsv,
  IMPORT_TARGET_IGNORE,
  MAX_IMPORT_ROWS,
  parseCsvFile,
  partitionImportRowsByTitle,
} from './importCsvUtils';
import { normalizeEnumOptionValues } from 'utilities/EnumOptions';
import { getPluralLabel, getSingularLabel } from 'utilities/Languages';
import { createNode as createNodeFromFactory } from 'utilities/NodeFactory';

// Services
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { listChildNodes, listNodesByDatabase } from 'services/nodes/NodeReadService';
import { setNodeDocs } from 'services/nodes/NodeWriteService';

// CSS
import './ImportModal.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ImportTemplateFieldsModal from 'collection/import/ImportTemplateFieldsModal';
import { toast } from 'react-toastify';

const MAX_ENUM_HINTS = 4;

/**
 * ImportModal
 *
 * Modal component for importing nodes from a CSV file. Provides a downloadable template,
 * upload preview, and best-effort import with error handling.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} setOpen - Function to set the open state of the modal.
 * @param {object} user - Current user context.
 * @param {string} typeKey - Type key for the nodes being imported.
 * @param {object} typeDef - Type definition for the nodes being imported.
 * @param {string} baseKey - Optional base key for the imported nodes.
 * @param {object} resolvedProperties - Resolved properties for the database type.
 * @param {function} getType - Function to get type definitions by key.
 * @param {object} [anchorNode] - Optional parent node. When set, imported rows are
 *   created as related nodes and edged to this anchor (node-scoped import).
 *
 * @returns {ReactNode} The ImportModal component.
 */
const ImportModal = React.memo(({
  isOpen,
  setOpen,
  user,
  typeKey,
  typeDef,
  baseKey,
  resolvedProperties,
  getType,
  anchorNode = null,
}) => {

  // ----- STATE ----- //

  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mappings, setMappings] = useState({});
  const [parseError, setParseError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [templateFieldsOpen, setTemplateFieldsOpen] = useState(false);

  // ----- MEMOS ----- //

  const singularLabel = useMemo(() => {
    return getSingularLabel(typeDef, typeKey) || 'Item';
  }, [typeDef, typeKey]);

  const pluralLabel = useMemo(() => {
    return getPluralLabel(typeDef, typeKey) || 'Items';
  }, [typeDef, typeKey]);

  const titleFieldLabel = useMemo(() => {
    return singularLabel;
  }, [singularLabel]);

  const allFields = useMemo(() => {
    return buildImportableFields({
      resolvedProperties,
      typeDef,
      getType,
    });
  }, [resolvedProperties, typeDef, getType]);

  const supportedFields = useMemo(() => {
    return allFields.filter((field) => field.supported);
  }, [allFields]);

  const fieldsByKey = useMemo(() => {
    const map = new Map();
    supportedFields.forEach((field) => map.set(field.key, field));
    return map;
  }, [supportedFields]);

  const preview = useMemo(() => {
    return buildImportPreview({
      rows,
      headers,
      mappings,
      fieldsByKey,
      titleFieldLabel,
    });
  }, [rows, headers, mappings, fieldsByKey, titleFieldLabel]);

  const ignoredColumnCount = useMemo(() => {
    return headers.reduce((count, header) => {
      return count + ((mappings?.[header] || IMPORT_TARGET_IGNORE) === IMPORT_TARGET_IGNORE ? 1 : 0);
    }, 0);
  }, [headers, mappings]);

  const templateFieldHints = useMemo(() => {
    return supportedFields
      .filter((field) => field.type === 'enum')
      .map((field) => ({
        key: field.key,
        label: field.label,
        options: normalizeEnumOptionValues(field.options || []),
      }))
      .filter((field) => field.options.length > 0)
      .slice(0, MAX_ENUM_HINTS);
  }, [supportedFields]);

  const hasReviewIssues = useMemo(() => {
    if (!headers.length) return false;
    return ignoredColumnCount > 0 || preview.skippedRows.length > 0 || preview.validRows.length === 0;
  }, [headers.length, ignoredColumnCount, preview.skippedRows.length, preview.validRows.length]);

  // ----- UTILITIES ----- //

  const buildTemplateFileName = () => {
    const normalizedLabel = String(singularLabel || 'item')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return `${normalizedLabel || 'item'}-import-template.csv`;
  };

  /**
   * Downloads a CSV template for the given optional field keys.
   *
   * @param {string[]} selectedFieldKeys - Keys of optional fields to include.
   */
  const downloadTemplateCsv = (selectedFieldKeys = []) => {
    const selectedKeySet = new Set(
      (Array.isArray(selectedFieldKeys) ? selectedFieldKeys : [])
        .map((key) => String(key || '').trim())
        .filter(Boolean),
    );

    const selectedFields = supportedFields.filter((field) => selectedKeySet.has(field.key));
    const csv = buildImportTemplateCsv({
      fields: selectedFields,
      singularLabel,
      titleFieldLabel,
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = buildTemplateFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const reset = () => {
    setFileName('');
    setHeaders([]);
    setRows([]);
    setMappings({});
    setParseError('');
    setIsImporting(false);
    setTemplateFieldsOpen(false);
  };

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- REFS ----- //

  const fileInputRef = useRef(null);

  // ----- HANDLERS ----- //

  /**
 * Handles close.
 */

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  /**
   * Opens the template field picker before download.
   */
  const handleDownloadTemplate = () => {
    setTemplateFieldsOpen(true);
  };

  /**
   * Closes the template field picker.
   */
  const handleCloseTemplateFields = () => {
    setTemplateFieldsOpen(false);
  };

  /**
   * Downloads a template using the fields chosen in the picker.
   *
   * @param {string[]} selectedFieldKeys - Keys of optional fields to include.
   */
  const handleConfirmTemplateFields = (selectedFieldKeys) => {
    try {
      downloadTemplateCsv(selectedFieldKeys);
      setTemplateFieldsOpen(false);
    } catch (error) {
      console.error('Failed to download import template:', error);
      toast.error('Failed to download template.');
    }
  };

  /**
 * Handles browse click.
 */
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  /**
 * Handles file change.
 *
 * @param {*} event
 */
  const handleFileChange = async (event) => {
    const file = event?.target?.files?.[0];

    if (event?.target) event.target.value = '';
    if (!file) return;

    setParseError('');

    try {
      const parsed = await parseCsvFile(file);
      if (!parsed.ok) {
        setFileName('');
        setHeaders([]);
        setRows([]);
        setMappings({});
        setParseError(parsed.error || 'Failed to parse CSV file.');
        return;
      }

      const initialMappings = buildInitialMappings({
        headers: parsed.headers,
        fields: allFields,
        titleFieldLabel,
      });

      setFileName(String(file?.name || 'import.csv'));
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMappings(initialMappings);
    } catch (error) {
      setFileName('');
      setHeaders([]);
      setRows([]);
      setMappings({});
      setParseError(error?.message || 'Failed to read CSV file.');
    }
  };

  /**
   * Loads existing nodes used for title de-dupe.
   * Database import: same type in the base.
   * Node import: same type already linked to the anchor.
   *
   * @returns {Promise<object[]>}
   */
  const loadExistingNodesForDedupe = async () => {
    const cleanTypeKey = String(typeKey || '').trim();
    if (!cleanTypeKey) return [];

    const anchorKey = String(anchorNode?.key || '').trim();
    if (anchorKey) {
      const children = await listChildNodes(anchorKey, {
        baseKey: baseKey || anchorNode?.baseKey || null,
      });
      return (Array.isArray(children) ? children : []).filter((node) => (
        String(node?.type || '').trim() === cleanTypeKey
      ));
    }

    if (!baseKey) return [];
    return listNodesByDatabase({
      type: cleanTypeKey,
      baseKey,
    });
  };

  /**
   * Handles import.
   */
  const handleImport = async () => {
    if (!user?.key) {
      toast.error('Missing user context for import.');
      return;
    }

    if (!typeKey) {
      toast.error('Missing database type for import.');
      return;
    }

    if (!preview.validRows.length) {
      toast.error('No valid rows to import.');
      return;
    }

    const anchorKey = String(anchorNode?.key || '').trim();
    const isAnchored = Boolean(anchorKey);

    setIsImporting(true);

    try {
      const existingNodes = await loadExistingNodesForDedupe();
      const existingTitles = buildExistingImportTitleSet(existingNodes);
      const { toImport, skippedDuplicates } = partitionImportRowsByTitle({
        validRows: preview.validRows,
        existingTitles,
      });

      if (!toImport.length) {
        toast.info(
          skippedDuplicates.length > 0
            ? `Nothing new to import. Skipped ${skippedDuplicates.length} already existing.`
            : 'No valid rows to import.',
        );
        handleClose();
        return;
      }

      const entries = [];

      for (const row of toImport) {
        const node = await createNodeFromFactory(typeKey, user, baseKey || null);

        node.title = row.title;

        if (isAnchored) {
          node.parentTitle = anchorNode.title || '';
          node.parentType = anchorNode.type || null;
          node.profileRoot = false;
          node.contributorKey = user.key;
        } else {
          node.parentTitle = user.name || 'Profile';
          node.parentType = user.type || 'USER';
          node.profileRoot = true;
        }

        Object.entries(row.sectionData || {}).forEach(([sectionKey, values]) => {
          const base = node?.[sectionKey] && typeof node[sectionKey] === 'object'
            ? { ...node[sectionKey] }
            : {};

          node[sectionKey] = {
            ...base,
            ...(values || {}),
          };
        });

        entries.push({
          key: node.key,
          payload: node,
        });
      }

      await setNodeDocs(entries, { merge: false });

      const invalidSkipped = preview.skippedRows.length;
      const duplicateSkipped = skippedDuplicates.length;
      const skipSummaryParts = [];
      if (duplicateSkipped > 0) {
        skipSummaryParts.push(`${duplicateSkipped} already existing`);
      }
      if (invalidSkipped > 0) {
        skipSummaryParts.push(`${invalidSkipped} invalid`);
      }
      const skipSummary = skipSummaryParts.length > 0
        ? ` Skipped ${skipSummaryParts.join(', ')}.`
        : '';

      if (isAnchored) {
        const edgeBaseKey = baseKey || anchorNode?.baseKey || null;
        let linkedCount = 0;
        let linkFailedCount = 0;

        for (const entry of entries) {
          try {
            await upsertEdge(anchorKey, entry.key, { baseKey: edgeBaseKey });
            linkedCount += 1;
          } catch (linkError) {
            linkFailedCount += 1;
            console.error('Failed to link imported node:', entry.key, linkError);
          }
        }

        if (linkFailedCount > 0) {
          toast.warning(
            `Imported ${entries.length} rows. Linked ${linkedCount}. Failed to link ${linkFailedCount}.${skipSummary}`
          );
        } else {
          toast.success(`Imported ${entries.length} rows.${skipSummary}`);
        }
      } else {
        toast.success(`Imported ${entries.length} rows.${skipSummary}`);
      }

      handleClose();
    } catch (error) {
      console.error('Database import failed:', error);
      toast.error(error?.message || 'Import failed.');
    } finally {
      setIsImporting(false);
    }
  };
  if (!isOpen) return null;

  // ----- RENDER ----- //

  return (
    <>
      <Modal
        title={`Import ${pluralLabel}`}
        isOpen={isOpen}
        width={isMobile ? '100%' : 600}
        height="auto"
        maxHeight="90vh"
        onClose={handleClose}
      >
        <div className="import-modal-container">

          {/* IMPORT ERROR */}
          {parseError && (

            <div className="import-modal-error">{parseError}</div>
          )}

          {/* STEPS */}
          <div className="import-modal-steps">

            {/* STEP CARD */}
            <div className="import-modal-step-card">

              {/* STEP NUMBER */}
              <div
                className="import-modal-step-number"
              >
                1
              </div>

              {/* STEP CONTENT */}
              <div className="import-modal-step-content">

                {/* STEP TITLE */}
                <div className="import-modal-step-title">Download a ready-made CSV file for {pluralLabel}.</div>

                  {/* STEP HELPER */}
                  <div
                    className="import-modal-step-helper"
                  >
                      CSV (Comma Separated Values) files can be edited like spreadsheets.
                    </div>

                {/* STEP ACTION */}
                <div className="import-modal-step-action">
                  <Button
                    label="Download Template CSV"
                    iconKey="download"
                    enabled={!isImporting}
                    onClick={handleDownloadTemplate}
                  />
                </div>
              </div>
            </div>

          {/* STEP CARD */}
          <div className="import-modal-step-card">

            {/* STEP NUMBER */}
            <div
              className="import-modal-step-number"
            >
              2
            </div>

            {/* STEP CONTENT */}
            <div className="import-modal-step-content">

              {/* STEP TITLE */}
              <div className="import-modal-step-title">Fill it in using Excel, Google Sheets, or Numbers.</div>

              {/* STEP HELPER */}
              <div className="import-modal-step-helper">Once you have filled it in, save the sheet as a CSV file on your computer, and remember where you saved the file.</div>
            </div>
          </div>

          {/* STEP CARD */}
          <div className="import-modal-step-card">

            {/* STEP NUMBER */}
            <div
              className="import-modal-step-number"
            >
              3
            </div>

            {/* STEP CONTENT */}
            <div className="import-modal-step-content">

              {/* STEP TITLE */}
              <div className="import-modal-step-title">Upload your finished CSV file here.</div>

              {/* STEP ACTION */}
              <div className="import-modal-step-action">
                <Button
                  label="Upload CSV"
                  iconKey="upload"
                  enabled={!isImporting}
                  onClick={handleBrowseClick}
                />

                {/* FILE NAME */}
                <div className="import-modal-file-name">
                  {fileName ? `Selected file: ${fileName}` : 'No CSV selected yet.'}
                </div>

                {/* FILE INPUT */}
                <input
                  className="import-modal-file-input"
                  ref={fileInputRef}
                  id={anchorNode?.key ? `node-import-csv-input-${typeKey || 'item'}` : 'database-import-csv-input'}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                />
              </div>
            </div>
          </div>

          {/* STEP CARD */}
          <div className="import-modal-step-card">

            {/* STEP NUMBER */}
            <div
              className="import-modal-step-number"
            >
              4
            </div>

            {/* STEP CONTENT */}
            <div className="import-modal-step-content">

              {/* STEP TITLE */}
              <div className="import-modal-step-title">Review and approve.</div>

              {headers.length > 0 ? (
                <>

                  {/* STATS */}
                  <div className="import-modal-stats">

                    {/* STAT CARD */}
                    <div className="import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="import-modal-stat-label">Rows</div>

                      {/* STAT VALUE */}
                      <div className="import-modal-stat-value">{rows.length}</div>
                    </div>

                    {/* STAT CARD */}
                    <div className="import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="import-modal-stat-label">Valid rows</div>

                      {/* STAT VALUE */}
                      <div className="import-modal-stat-value">{preview.validRows.length}</div>
                    </div>

                    {/* STAT CARD */}
                    <div className="import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="import-modal-stat-label">Skipped rows</div>

                      {/* STAT VALUE */}
                      <div className="import-modal-stat-value">{preview.skippedRows.length}</div>
                    </div>
                  </div>

                  {/* WARNING */}
                  {ignoredColumnCount > 0 && (

                    <div className="import-modal-warning">
                      {ignoredColumnCount} column{ignoredColumnCount === 1 ? '' : 's'} were not recognized. Keep the template headers unchanged for the smoothest import.
                    </div>
                  )}

                  {/* REVIEW STATUS */}
                  <div className={`import-modal-review-status ${hasReviewIssues ? 'error' : 'valid'}`}>
                    <Icon
                      iconKey={hasReviewIssues ? 'close' : 'check'}
                      width={18}
                      height={18}
                      color={hasReviewIssues ? '#ff9090' : '#8ee6a3'}
                    />

                    {/* REVIEW STATUS TEXT */}
                    <div className="import-modal-review-status-text">
                      {hasReviewIssues
                        ? 'There is a problem with this upload. Review the summary above before importing.'
                        : 'Everything in this upload looks valid and ready to import.'}
                    </div>
                  </div>

                  {/* STEP ACTION */}
                  <div className="import-modal-step-action">
                    <Button
                      label={isImporting ? 'Importing...' : `Import ${preview.validRows.length} Rows`}
                      iconKey="upload"
                      height="34px"
                      fontSize="12px"
                      padding="0 12px"
                      enabled={!isImporting && preview.validRows.length > 0}
                      onClick={handleImport}
                    />
                  </div>
                </>
              ) : (

                <div className="import-modal-step-helper">Once uploaded, you can review its status before importing.</div>
              )}
            </div>
          </div>

        </div>

        {/* NOTES CARD */}
        <div className="import-modal-notes-card">

          {/* NOTES TITLE */}
          <div className="import-modal-notes-title">A few quick notes:</div>

          {/* NOTES LIST */}
          <ul className="import-modal-notes-list">

            {/* NOTE LINE */}
            <li className="import-modal-note-line">Max rows per import: {MAX_IMPORT_ROWS}.</li>

            {/* NOTE LINE */}
            <li className="import-modal-note-line">Do not rename the header row or remove any columns.</li>

            {/* NOTE LINE */}
            <li className="import-modal-note-line">Replace or delete the example row before importing.</li>

            {/* NOTE LINE */}
            <li className="import-modal-note-line">Keep the {titleFieldLabel} column filled in for every row.</li>

            {/* NOTE LINE */}
            <li className="import-modal-note-line">Rows with a title that already exists are skipped.</li>
            {templateFieldHints.map((field) => (

              <li className="import-modal-note-line" key={field.key}>
                For {field.label}, the options are: {field.options.join(', ')}.
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>

      <ImportTemplateFieldsModal
        isOpen={templateFieldsOpen}
        titleFieldLabel={titleFieldLabel}
        fields={supportedFields}
        onClose={handleCloseTemplateFields}
        onConfirm={handleConfirmTemplateFields}
      />
    </>
  );
});

// Dev Tools
ImportModal.displayName = 'ImportModal';

export default ImportModal;
